import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import {
  Link,
  Progress,
  ErrorProgress,
  Button,
  Skeleton,
} from 'next/components/ui';

const FLOWS_THRESHOLD = 10;
const SIGNUP_SUBJECT = 'Sign Up for Appcues';
const SIGNUP_MESSAGE = [
  'Hi,',
  "I'd like to sign up for an Appcues subscription.",
  'Thanks!',
].join('\n\n');

const Wrapper = styled.div`
  text-align: center;

  ${Skeleton} {
    width: 140px;
    height: 40px;
    margin: 0 auto;
  }

  ${Progress} {
    height: 12px;
    margin-bottom: 10px;
  }

  ${Button} {
    margin: 0 12px;

    &:hover {
      color: var(--white);
    }
  }
`;

const Counter = styled.div`
  font-weight: var(--bold);
  color: var(--heading-primary);
  font-size: var(--regular);
`;

const Message = styled.div`
  color: var(--heading-primary);
  font-size: var(--regular);
  margin-bottom: 10px;
`;

const UpgradeLink = styled(Link)`
  color: var(--primary);
  font-weight: var(--bold);

  &:hover {
    color: var(--heading-primary);
  }
`;

const TrialMeter = ({
  className,
  createdAt,
  days = 0,
  email,
  expired = false,
  limit = 0,
  name,
  shown,
  onLoad,
}) => {
  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  const handleUpgrade = () => {
    try {
      /* eslint-disable new-cap */
      window.Beacon('prefill', {
        name,
        email,
        subject: SIGNUP_SUBJECT,
        text: SIGNUP_MESSAGE,
      });
      window.Beacon('navigate', '/ask/');
      window.Beacon('open');
      /* eslint-enable new-cap */
    } catch {
      // eslint-disable-next-line no-empty
      // If beacon fail we don't care since we are going to
      // subscription page anyway
    }
  };

  // Display a loader until the total shown flows is resolved
  if (shown == null) {
    return (
      <Wrapper>
        <Skeleton data-testid="loader" />
      </Wrapper>
    );
  }

  const hasFlows = limit - shown > 0;
  const remainingDays = Math.ceil(
    moment(createdAt).add(days, 'days').diff(moment(), 'days', true)
  );
  const hasEnded = expired || (!hasFlows && remainingDays <= 0);

  if (hasEnded) {
    return (
      <Wrapper className={className}>
        <Message>You&apos;ve reached your trial limit</Message>
        <Button forwardedAs={Link} to="/subscription" onClick={handleUpgrade}>
          Upgrade now
        </Button>
      </Wrapper>
    );
  }

  const progress = hasFlows ? shown / limit : (days - remainingDays) / days;
  const percentage = Math.round(progress * 100, 2);

  return (
    <Wrapper className={className}>
      {hasFlows && (
        <Counter>
          {shown} of {limit}
        </Counter>
      )}
      <Message>
        {hasFlows
          ? 'Flows shown in trial'
          : `Trial expires in ${remainingDays} day${
              remainingDays > 1 ? 's' : ''
            }`}
      </Message>
      {!hasFlows || limit - shown > FLOWS_THRESHOLD ? (
        <Progress value={percentage} />
      ) : (
        <ErrorProgress value={percentage} />
      )}
      <UpgradeLink to="/subscription" onClick={handleUpgrade}>
        Upgrade now
      </UpgradeLink>
    </Wrapper>
  );
};

TrialMeter.propTypes = {
  // Classes mainly for style forwarding
  className: PropTypes.string,
  // The date the account was created
  createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // The amount of trial days
  days: PropTypes.number,
  // The user email
  email: PropTypes.string,
  // A flag that indicates if the trial has explicitly ended through supportcues
  expired: PropTypes.bool,
  // The amount of max flows shown
  limit: PropTypes.number,
  // The user name
  name: PropTypes.string,
  // The amount of flows shown
  shown: PropTypes.number,
  // Invoke the analytics metrics to know the amount of flows shown
  onLoad: PropTypes.func,
};

export default styled(TrialMeter)``;
